import { BackdropProps, InputAdornment, TextFieldProps } from '@mui/material';
import {
  FocusEvent,
  FocusEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { IconArrowRight, IconCalendar } from '@u21/tabler-icons';
import { U21InputGroup } from 'app/shared/u21-ui/components/display/U21InputGroup';
import { ClearButton } from 'app/shared/u21-ui/components/input/text-field/styles';
import { usePickersContext } from '@mui/x-date-pickers';
import { createPortal } from 'react-dom';
import { StyledBackdrop } from 'app/shared/u21-ui/components/input/select/styles';
import { U21TextField } from 'app/shared/u21-ui/components/input/text-field/U21TextField';
import { format } from 'date-fns';
import useForkRef from '@mui/utils/useForkRef';
import {
  CustomizableFieldProps,
  DateRangeValue,
} from 'app/shared/u21-ui/components/input/date-picker/models';
import { getDOMProps } from 'app/shared/utils/react';

interface Props {
  autoFocus?: boolean;
  backdrop?: boolean;
  backdropProps?: Omit<BackdropProps, 'open'>;
  clearable: boolean;
  dateFormat: string;
  disabled?: boolean;
  endFieldProps?: CustomizableFieldProps;
  error?: boolean;
  idCustom?: string; // custom prop to avoid clashing with MUI X
  InputProps?: TextFieldProps['InputProps']; // comes from MUI X
  label?: string;
  maxDate: Date;
  maxDateInputProps?: TextFieldProps['inputProps'];
  minDate: Date;
  minDateInputProps?: TextFieldProps['inputProps'];
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>; // comes from MUI X
  onBlurCustom?: FocusEventHandler<HTMLInputElement>; // custom prop to avoid clashing with MUI X
  onChange: (value: DateRangeValue) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onSelectedSectionsChange?: unknown; // comes from MUI X, but not used in this component
  required?: boolean;
  startFieldProps?: CustomizableFieldProps;
  value: DateRangeValue;
}

const DateRangeInputFieldInternal = forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const {
      autoFocus,
      backdrop,
      backdropProps,
      clearable,
      dateFormat,
      disabled,
      endFieldProps,
      error,
      idCustom: id,
      InputProps,
      label,
      maxDate,
      maxDateInputProps,
      minDate,
      minDateInputProps,
      name,
      onBlur,
      onBlurCustom,
      onChange,
      onFocus,
      onSelectedSectionsChange,
      required,
      startFieldProps,
      value,
      ...rest
    } = props;

    const [start = null, end = null] = value;
    const hasClearIcon =
      clearable && !disabled && (Boolean(start) || Boolean(end));

    const pickersContext = usePickersContext();

    const endInternalInputRef = useRef<HTMLInputElement>(null);
    const startInternalInputRef = useRef<HTMLInputElement>(null);

    const [endInputValue, setEndInputValue] = useState<string>('');
    const [startInputValue, setStartInputValue] = useState<string>('');

    const [startValue, endValue] = value;

    const isDateValid = useCallback(
      (maybe: Date | string): boolean => {
        const date = new Date(maybe);
        if (isNaN(date.getTime())) {
          return false;
        }
        return date <= maxDate && date >= minDate;
      },
      [maxDate, minDate],
    );

    const startValid = useMemo(
      () => (startInputValue ? isDateValid(startInputValue) : true),
      [isDateValid, startInputValue],
    );
    const endValid = useMemo(
      () => (endInputValue ? isDateValid(endInputValue) : true),
      [endInputValue, isDateValid],
    );

    useEffect(() => {
      if (startValue) {
        if (isDateValid(startValue)) {
          // temporary fix since there are instances where value is string, SC-58419 for cleanup
          setStartInputValue(format(new Date(startValue), dateFormat));
        }
      } else {
        // if value is gone, and previous value was valid, clear
        // don't clear otherwise to show red error to user
        setStartInputValue((prevInputValue) => {
          if (isDateValid(prevInputValue)) {
            return '';
          }
          return prevInputValue;
        });
      }
    }, [dateFormat, isDateValid, startValue]);

    useEffect(() => {
      if (endValue) {
        if (isDateValid(endValue)) {
          // temporary fix since there are instances where value is string, SC-58419 for cleanup
          setEndInputValue(format(new Date(endValue), dateFormat));
        }
      } else {
        // if value is gone, and previous value was valid, clear
        // don't clear otherwise to show red error to user
        setEndInputValue((prevInputValue) => {
          if (isDateValid(prevInputValue)) {
            return '';
          }
          return prevInputValue;
        });
      }
    }, [dateFormat, isDateValid, endValue]);

    const combinedRef = useForkRef(ref, InputProps?.ref);

    return (
      <>
        {backdrop &&
          createPortal(
            <StyledBackdrop
              invisible
              {...backdropProps}
              open={pickersContext.open}
            />,
            document.body,
          )}
        <StyledU21InputGroup
          borders={false}
          disabled={disabled}
          error={error}
          ref={combinedRef}
          {...getDOMProps(rest)}
        >
          <StyledU21TextField
            autoFocus={autoFocus}
            clearable={false}
            disabled={disabled}
            error={error || !startValid}
            id={id}
            inputProps={minDateInputProps}
            inputRef={startInternalInputRef}
            label={label}
            name={name ? `${name}-start` : undefined}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              onBlurCustom?.(e);
              onBlur?.(e);
              // use e.target.value instead of inputValue because inputValue is delayed by 300ms
              const targetValue = e.target.value;
              if (targetValue) {
                if (isDateValid(targetValue)) {
                  if (end && new Date(targetValue) > new Date(end)) {
                    // clear end value if range is invalid
                    onChange([
                      isDateValid(targetValue)
                        ? new Date(targetValue)
                        : undefined,
                      undefined,
                    ]);
                    endInternalInputRef.current?.focus();
                  } else {
                    onChange([
                      isDateValid(targetValue)
                        ? new Date(targetValue)
                        : undefined,
                      end,
                    ]);
                  }
                } else {
                  onChange([start, undefined]);
                  endInternalInputRef.current?.focus();
                }
              }
            }}
            onChange={(newValue: string = '') => {
              setStartInputValue(newValue);
            }}
            onFocus={onFocus}
            onKeyDown={(e) => {
              // blur on enter and focus on end if empty
              if (e.key === 'Enter') {
                startInternalInputRef.current?.blur();
                if (!end) {
                  endInternalInputRef.current?.focus();
                }
              }
            }}
            placeholder="Start date"
            required={required}
            startIcon={<IconCalendar />}
            value={startInputValue}
            {...startFieldProps}
          />
          <StyledIconArrowRight $disabled={disabled} />
          <StyledU21TextField
            $right
            clearable={false}
            disabled={disabled}
            error={error || !endValid}
            inputProps={maxDateInputProps}
            InputProps={{
              endAdornment: hasClearIcon && (
                <InputAdornment position="end">
                  <ClearButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange([undefined, undefined]);
                      startInternalInputRef.current?.focus();
                    }}
                  />
                </InputAdornment>
              ),
            }}
            inputRef={endInternalInputRef}
            name={name ? `${name}-end` : undefined}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              onBlur?.(e);
              // use e.target.value instead of inputValue because inputValue is delayed by 300ms
              const targetValue = e.target.value;
              if (targetValue) {
                if (isDateValid(targetValue)) {
                  if (start && new Date(targetValue) < new Date(start)) {
                    // clear start value if range is invalid
                    onChange([
                      undefined,
                      isDateValid(targetValue)
                        ? new Date(targetValue)
                        : undefined,
                    ]);
                    startInternalInputRef.current?.focus();
                  } else {
                    onChange([
                      start,
                      isDateValid(targetValue)
                        ? new Date(targetValue)
                        : undefined,
                    ]);
                  }
                } else {
                  onChange([start, undefined]);
                }
              }
            }}
            onChange={(newValue: string = '') => {
              setEndInputValue(newValue);
            }}
            onFocus={onFocus}
            onKeyDown={(e) => {
              // blur on enter and focus on end if empty
              if (e.key === 'Enter') {
                endInternalInputRef.current?.blur();
                if (!start) {
                  startInternalInputRef.current?.focus();
                }
              }
            }}
            placeholder="End date"
            value={endInputValue}
            {...endFieldProps}
          />
        </StyledU21InputGroup>
      </>
    );
  },
);

export const DateRangeInputField = Object.assign(DateRangeInputFieldInternal, {
  // mui uses this to figure out whether to pass this component single or multi input props
  fieldType: 'single-input',
});

const StyledU21InputGroup = styled(U21InputGroup)`
  position: relative;

  &:hover {
    ${ClearButton} {
      visibility: visible;
    }
  }
`;

const StyledU21TextField = styled(U21TextField)<{ $right?: boolean }>`
  ${(props) =>
    !props.disabled &&
    css`
      .MuiInputBase-root {
        cursor: pointer;

        .MuiInputBase-input {
          cursor: pointer;
        }
      }
    `}

  .MuiInputBase-input {
    text-align: center;
    ${(props) =>
      props.$right
        ? css`
            padding-left: 6px;
          `
        : css`
            padding-right: 6px;
          `}
  }
`;

const StyledIconArrowRight = styled(IconArrowRight)<{ $disabled?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${(props) =>
    props.$disabled
      ? props.theme.palette.text.disabled
      : props.theme.palette.text.secondary};
`;
